import React, { useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Box, Button, IconButton, Typography, useMediaQuery } from '@mui/material';
import { alpha, darken, Theme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useGlobalState } from '../../global/context/global-context';
import { getInstitutionalBanners, IInstitutionalBannerAction, isChamber } from '../../home/institutional-banners';
import { MOTION, RADIUS, SHADOWS } from '../../../theme/tokens';

const SLIDE_DURATION_MS = 8000;
const SWIPE_THRESHOLD_PX = 50;

const rise = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const floatIn = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const fill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

// Controles sobre o fundo escuro, com alvo de toque de 44px (mesmo tamanho do HeroBanner)
const controlSx = {
  width: 44,
  height: 44,
  color: 'common.white',
  border: '1px solid rgba(255, 255, 255, 0.4)',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.16)', borderColor: 'common.white' },
  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
} as const;

const isKeyboardFocus = (element: Element): boolean => {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
};

function BannerAction({ action, variant }: { action: IInstitutionalBannerAction; variant: 'primary' | 'secondary' }) {
  const { state, city } = useParams<{ state?: string; city?: string }>();
  const isExternal = /^https?:\/\//i.test(action.url);

  const sx = {
    fontWeight: 700,
    fontSize: '1rem',
    textTransform: 'none',
    px: 2.75,
    minHeight: 48,
    '& .MuiButton-endIcon': { transition: `transform ${MOTION.base} ${MOTION.easeOut}` },
    '&:hover .MuiButton-endIcon': { transform: 'translateX(3px)' },
    '&:focus-visible': { outline: '3px solid #fff', outlineOffset: 3 },
    ...(variant === 'primary'
      ? {
          backgroundColor: 'common.white',
          color: 'brand.deep',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
        }
      : {
          color: 'common.white',
          borderColor: 'rgba(255, 255, 255, 0.55)',
          '&:hover': { borderColor: 'common.white', backgroundColor: 'rgba(255, 255, 255, 0.12)' },
        }),
  } as const;

  const buttonProps = {
    variant: variant === 'primary' ? ('contained' as const) : ('outlined' as const),
    endIcon: variant === 'primary' ? <ArrowForwardIcon /> : undefined,
    sx,
  };

  // Ouvidoria e SIC são sistemas externos: abrem em nova aba
  if (isExternal) {
    return (
      <Button {...buttonProps} component="a" href={action.url} target="_blank" rel="noopener noreferrer">
        {action.label}
        <Box component="span" sx={visuallyHiddenSx}> (abre em nova aba)</Box>
      </Button>
    );
  }

  const path = action.url.replace(/^\//, '');
  return (
    <Button {...buttonProps} component={RouterLink} to={state && city ? `/${state}/${city}/${path}` : `/${path}`}>
      {action.label}
    </Button>
  );
}

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/**
 * Carrossel institucional do topo da home, exibido quando a entidade não tem banners cadastrados no APM.
 * Conteúdo de prefeitura ou câmara conforme o tipo da entidade; cores do tema (brand.deep garante 7:1 com o texto branco).
 */
export default function InstitutionalHeroBanner() {
  const { applicationData } = useGlobalState();
  const entity = applicationData?.entity;
  const banners = useMemo(() => getInstitutionalBanners(entity), [entity]);
  const entityLabel = entity?.nickName || (isChamber(entity) ? 'Câmara Municipal' : 'Prefeitura Municipal');

  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });
  const [active, setActive] = useState(0);
  // Autoplay desligado para quem pede menos movimento; o usuário pode iniciar pelo botão
  const [userPaused, setUserPaused] = useState(prefersReducedMotion);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const total = banners.length;
  const paused = userPaused || hovered || focused;
  const goTo = (index: number) => setActive((index + total) % total);

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const deltaX = event.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;
    goTo(deltaX < 0 ? active + 1 : active - 1);
  };

  if (total === 0) return null;

  return (
    <Box
      role="region"
      aria-roledescription="carrossel"
      aria-label="Destaques institucionais"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Pausa só com foco de teclado (APG); clique do mouse em um controle não deve travar o carrossel
      onFocus={(event) => setFocused(isKeyboardFocus(event.target))}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
      }}
      onTouchStart={(event) => (touchStartX.current = event.touches[0].clientX)}
      onTouchEnd={handleTouchEnd}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        isolation: 'isolate',
        borderRadius: `${RADIUS.card}px`,
        color: 'common.white',
        backgroundColor: 'brand.deep',
        backgroundImage: (theme: Theme) =>
          `linear-gradient(110deg, ${theme.palette.brand.deep} 0%, ${theme.palette.brand.deep} 42%, ${darken(theme.palette.primary.main, 0.35)} 100%)`,
        // Brilho na cor da entidade atrás do painel
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: { xs: '30% -40% -40% 10%', md: '-30% -15% -30% 45%' },
          background: (theme: Theme) => `radial-gradient(closest-side, ${alpha(theme.palette.primary.main, 0.5)}, transparent)`,
          zIndex: -1,
          pointerEvents: 'none',
        },
        // Grade de pontos à direita
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: { xs: '0 0 0 30%', md: '0 0 0 48%' },
          backgroundImage: 'radial-gradient(rgba(255, 255, 255, 0.16) 1.2px, transparent 1.6px)',
          backgroundSize: '22px 22px',
          maskImage: 'linear-gradient(to left, #000 10%, transparent 85%)',
          WebkitMaskImage: 'linear-gradient(to left, #000 10%, transparent 85%)',
          zIndex: -1,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Anéis concêntricos decorativos */}
      <Box
        component="svg"
        viewBox="0 0 720 720"
        aria-hidden
        sx={{
          position: 'absolute',
          right: { xs: -320, md: -180 },
          bottom: { xs: -380, md: -260 },
          width: 720,
          height: 720,
          zIndex: -1,
          pointerEvents: 'none',
          color: 'rgba(255, 255, 255, 0.09)',
        }}
      >
        {[120, 200, 280, 360].map((radius) => (
          <circle key={radius} cx="360" cy="360" r={radius - 1} fill="none" stroke="currentColor" strokeWidth="1.5" />
        ))}
      </Box>

      {/* Slides empilhados na mesma célula: a altura acompanha o maior e a troca é um crossfade */}
      <Box aria-live={userPaused ? 'polite' : 'off'} sx={{ display: 'grid', '& > *': { gridArea: '1 / 1' } }}>
        {banners.map((banner, index) => {
          const isActive = index === active;
          const BannerIcon = banner.icon;
          const enter = isActive && !prefersReducedMotion;

          return (
            <Box
              key={banner.key}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} de ${total}: ${banner.tab}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' },
                alignItems: 'center',
                gap: { md: 5, lg: 7 },
                minHeight: { md: 460 },
                px: { xs: 2.5, sm: 5, lg: 9 },
                pt: { xs: 4.5, md: 7 },
                pb: { xs: 13, md: 15 },
                opacity: isActive ? 1 : 0,
                visibility: isActive ? 'visible' : 'hidden',
                transition: prefersReducedMotion
                  ? 'none'
                  : isActive
                    ? 'opacity 0.6s ease, visibility 0s'
                    : 'opacity 0.6s ease, visibility 0s linear 0.6s',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 2.5,
                  minWidth: 0,
                  '& > *': enter ? { animation: `${rise} 0.7s ${MOTION.easeOut} both` } : {},
                  '& > *:nth-of-type(2)': enter ? { animationDelay: '0.07s' } : {},
                  '& > *:nth-of-type(3)': enter ? { animationDelay: '0.14s' } : {},
                  '& > *:nth-of-type(4)': enter ? { animationDelay: '0.21s' } : {},
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 0.75,
                    pl: 0.75,
                    pr: 2,
                    borderRadius: 999,
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    border: '1px solid rgba(255, 255, 255, 0.24)',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: 'common.white',
                      color: 'brand.deep',
                      '& svg': { fontSize: '1.05rem' },
                    }}
                  >
                    <BannerIcon />
                  </Box>
                  {banner.eyebrow}
                </Box>

                <Typography
                  variant="inherit"
                  component="h2"
                  sx={{
                    m: 0,
                    maxWidth: '20ch',
                    fontFamily: (theme) => theme.typography.h1.fontFamily,
                    fontSize: 'clamp(1.75rem, 1.1rem + 2.2vw, 3rem)',
                    fontWeight: 600,
                    lineHeight: 1.12,
                    letterSpacing: '-0.02em',
                    textWrap: 'balance',
                    color: 'common.white',
                  }}
                >
                  {banner.title}
                </Typography>

                <Typography
                  variant="inherit"
                  component="p"
                  sx={{
                    m: 0,
                    maxWidth: '54ch',
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    lineHeight: 1.6,
                    color: 'rgba(255, 255, 255, 0.88)',
                  }}
                >
                  {banner.description}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 0.5 }}>
                  <BannerAction action={banner.primaryAction} variant="primary" />
                  {banner.secondaryAction && <BannerAction action={banner.secondaryAction} variant="secondary" />}
                </Box>
              </Box>

              {/* Painel com o que o cidadão encontra e a base legal */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'block' },
                  position: 'relative',
                  justifySelf: 'end',
                  width: 'min(100%, 420px)',
                  mr: 1.5,
                  animation: enter ? `${floatIn} 0.9s ${MOTION.easeOut} 0.12s both` : 'none',
                  // Cartões translúcidos atrás do painel, para dar profundidade
                  '&::before, &::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '22px',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                  },
                  '&::before': { backgroundColor: 'rgba(255, 255, 255, 0.1)', transform: 'rotate(-6deg) translate(-18px, 6px)' },
                  '&::after': { backgroundColor: 'rgba(255, 255, 255, 0.06)', transform: 'rotate(4deg) translate(16px, 10px)' },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    p: 2.75,
                    borderRadius: `${RADIUS.block + 4}px`,
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: `0 32px 64px -28px rgba(2, 6, 23, 0.6), ${SHADOWS.sm}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, pb: 2.25, mb: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        flexShrink: 0,
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 52,
                        height: 52,
                        borderRadius: '14px',
                        color: 'common.white',
                        backgroundImage: (theme: Theme) =>
                          `linear-gradient(135deg, ${theme.palette.brand.text}, ${theme.palette.brand.deep})`,
                        '& svg': { fontSize: '1.6rem' },
                      }}
                    >
                      <BannerIcon />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography
                        variant="inherit"
                        component="strong"
                        sx={{ fontFamily: (theme) => theme.typography.h1.fontFamily, fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.3 }}
                      >
                        {banner.panelTitle}
                      </Typography>
                      <Typography variant="inherit" component="span" noWrap sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                        {entityLabel}
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="ul" sx={{ display: 'flex', flexDirection: 'column', gap: 1, m: 0, p: 0, listStyle: 'none' }}>
                    {banner.highlights.map(({ label, icon: HighlightIcon }) => (
                      <Box
                        component="li"
                        key={label}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 1.25,
                          px: 1.5,
                          borderRadius: `${RADIUS.card}px`,
                          backgroundColor: 'background.subtle',
                          border: 1,
                          borderColor: 'grey.100',
                          fontSize: '0.9375rem',
                          fontWeight: 600,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            flexShrink: 0,
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 34,
                            height: 34,
                            borderRadius: '10px',
                            backgroundColor: 'brand.surfaceStrong',
                            color: 'brand.text',
                            '& svg': { fontSize: '1.15rem' },
                          }}
                        >
                          <HighlightIcon />
                        </Box>
                        {label}
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    zIndex: 2,
                    left: -28,
                    bottom: -18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    maxWidth: 'calc(100% + 28px)',
                    py: 1.25,
                    px: 1.75,
                    borderRadius: `${RADIUS.card}px`,
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    color: 'common.white',
                    backgroundColor: 'brand.deep',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    boxShadow: '0 16px 32px -16px rgba(2, 6, 23, 0.7)',
                    '& svg': { fontSize: '1.1rem', flexShrink: 0 },
                  }}
                >
                  <VerifiedIcon />
                  {banner.legalNote}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Navegação: abas com progresso + anterior/pausar/próximo */}
      <Box
        sx={{
          position: 'absolute',
          left: { xs: 20, sm: 40, lg: 72 },
          right: { xs: 20, sm: 40, lg: 72 },
          bottom: { xs: 20, md: 32 },
          display: 'flex',
          alignItems: 'flex-end',
          gap: { xs: 1.5, md: 3 },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'minmax(0, 1fr)',
            gap: 1.75,
            maxWidth: 760,
            minWidth: 0,
          }}
        >
          {banners.map((banner, index) => {
            const isActive = index === active;
            return (
              <Box
                key={banner.key}
                component="button"
                type="button"
                aria-label={`Exibir destaque ${index + 1} de ${total}: ${banner.tab}`}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => goTo(index)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  minWidth: 0,
                  pt: 1.25,
                  pb: 1.5,
                  px: 0,
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '0.8125rem',
                  fontWeight: isActive ? 700 : 600,
                  textAlign: 'left',
                  color: isActive ? 'common.white' : 'rgba(255, 255, 255, 0.68)',
                  transition: `color ${MOTION.base}`,
                  '&:hover': { color: 'common.white' },
                  '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 4, borderRadius: '4px' },
                }}
              >
                <Box
                  component="span"
                  sx={{ display: { xs: 'none', md: 'block' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {banner.tab}
                </Box>
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    height: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.22)',
                  }}
                >
                  {isActive && total > 1 && (
                    // O fim da animação avança o carrossel (pausar a animação pausa o carrossel);
                    // parado pelo usuário, a barra fica cheia só para indicar o slide atual. key reinicia a animação.
                    <Box
                      component="span"
                      key={`${active}-${userPaused}`}
                      onAnimationEnd={() => goTo(active + 1)}
                      sx={{
                        display: 'block',
                        height: '100%',
                        backgroundColor: 'common.white',
                        transformOrigin: 'left center',
                        animation: userPaused ? 'none' : `${fill} ${SLIDE_DURATION_MS}ms linear forwards`,
                        animationPlayState: paused ? 'paused' : 'running',
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {total > 1 && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: 'auto' }}>
            <IconButton onClick={() => goTo(active - 1)} aria-label="destaque anterior" sx={controlSx}>
              <KeyboardArrowLeft />
            </IconButton>
            <IconButton
              onClick={() => setUserPaused((current) => !current)}
              aria-label={userPaused ? 'retomar rotação dos destaques' : 'pausar rotação dos destaques'}
              sx={controlSx}
            >
              {userPaused ? <PlayArrowIcon /> : <PauseIcon />}
            </IconButton>
            <IconButton onClick={() => goTo(active + 1)} aria-label="próximo destaque" sx={controlSx}>
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
